import { useLocalObservable } from 'mobx-react-lite';
import { useEffect } from 'react';

import { useTranslation } from '@gem/modules/i18n';

import { User } from 'APP/store/Users/User/User';
import Entities from 'STORE';

import {
  ICallSelectSpeakersContactItem,
  ICallSelectSpeakersTargetData,
} from '../CallSelectSpeakersPopup.types';

export interface ICallSelectSpeakersListParams {
  searchValue: string;
  selectedSpeakerIds: Set<string>;
  onSelectSpeaker(userId: string): void;
}

interface ICallSelectSpeakersListPresenter {
  searchValue: string;
  contacts: User[];
  contactsItems: ICallSelectSpeakersContactItem[];
  content: ICallSelectSpeakersTargetData[];
  infoBlockDescriptions: string[];
  keyExtractor(item: ICallSelectSpeakersTargetData): string;
  setSearchValue(value: string): void;
}

export const useCallSelectSpeakersListPresenter = (
  params: ICallSelectSpeakersListParams
): ICallSelectSpeakersListPresenter => {
  const { t } = useTranslation();
  const { selectedSpeakerIds, onSelectSpeaker } = params;

  const presenter = useLocalObservable<ICallSelectSpeakersListPresenter>(() => ({
    searchValue: '',

    get contacts(): User[] {
      return Entities.usersStore.getFilteredContacts(
        [],
        [],
        presenter.searchValue.toLowerCase(),
        true
        // TODO remove as unknown as User[] when User store will be refactored on typescript
      ) as unknown as User[];
    },

    get contactsItems(): ICallSelectSpeakersContactItem[] {
      return presenter.contacts.map((contact) => ({
        id: contact.id,
        avatarTitle: contact.avatarTitle || '',
        avatarUrl: contact.avatarUrl || '',
        avatarColorIndex: contact.avatarColorIndex,
        blockedByMe: contact.isBlockedByMe,
        blockedMe: contact.isBlockedMe,
        verified: contact.verified,
      }));
    },

    get content(): ICallSelectSpeakersTargetData[] {
      return [...presenter.contactsItems].map((contactItem) => {
        const isSelected = selectedSpeakerIds.has(contactItem.id);

        return {
          data: contactItem,
          isSelected,
          onClick: () => onSelectSpeaker(contactItem.id),
        };
      });
    },

    get infoBlockDescriptions(): string[] {
      return [t('no_results_found')];
    },

    keyExtractor(item: ICallSelectSpeakersTargetData): string {
      return item.data.id;
    },

    mapContactToItem(contact: User): ICallSelectSpeakersContactItem {
      return {
        id: contact.id,
        avatarTitle: contact.avatarTitle || '',
        avatarUrl: contact.avatarUrl || '',
        avatarColorIndex: contact.avatarColorIndex,
        blockedByMe: contact.isBlockedByMe,
        blockedMe: contact.isBlockedMe,
        verified: contact.verified,
      };
    },

    setSearchValue(value: string): void {
      presenter.searchValue = value;
    },
  }));

  useEffect(() => {
    presenter.setSearchValue(params.searchValue);
  }, [params.searchValue]);

  return presenter;
};
